import {
  BookingApiStatusEnum,
  InvoiceApiTypeEnum,
} from '@revolugo/booking-api-client'
import {
  AccountingDocumentType,
  BookingStatus,
} from '@revolugo/graphql/private'

import {
  HotelRoomOfferAdapter,
  type HotelRoomOfferAdapterProps,
} from './hotel-room-offer.ts'

import type {
  BookingApi,
  CancellationPolicyApi,
  HotelRoomOfferApi,
} from '@revolugo/booking-api-client'
import type {
  Booking,
  CancellationPolicy,
  CurrencyGraphQl,
  Customer,
  HotelBooking,
  Invoice,
} from '@revolugo/graphql/private'

export interface BookingAdapterProps
  extends Pick<
      HotelBooking,
      | 'status'
      | 'checkOutDate'
      | 'checkInDate'
      | 'adultCount'
      | 'cancellationPolicies'
      | 'children'
      | 'hotelId'
    >,
    Pick<Booking, 'reference' | 'currency' | 'invoices'> {
  customer: Pick<Customer, 'firstName' | 'lastName'>
  event: string
  hotelRoomOffer: HotelRoomOfferAdapterProps
  taxAmount: number
  taxIncludedPrice: number
  price: number
}

export class BookingAdapter {
  public bookingApi: BookingApi
  public hotelRoomOffer: HotelRoomOfferApi

  constructor(bookingApi: BookingApi) {
    this.bookingApi = bookingApi
    this.hotelRoomOffer = bookingApi.hotelRoomOffer
  }

  public toHotelBooking(): BookingAdapterProps {
    const hotelRoomOfferAdapter = new HotelRoomOfferAdapter(this.hotelRoomOffer)
    const hotelRoomOffer = hotelRoomOfferAdapter.toHotelRoomOffer()
    return {
      ...this.bookingApi,
      cancellationPolicies:
        this.mapCancellationPolicyApiToCancellationPolicies(),
      children: this.bookingApi.children!,
      currency: this.bookingApi.currency as CurrencyGraphQl,
      customer: this.mapCustomerApiToCustomer(),
      event: this.bookingApi.event?.name ?? '',
      hotelRoomOffer,
      invoices: this.mapInvoicesApiToInvoices(),
      price: this.bookingApi.taxIncludedPrice - this.bookingApi.taxAmount,
      reference: this.bookingApi.reference,
      status: this.mapBookingStatusApiToBookingStatus(),
      taxIncludedPrice: this.bookingApi.taxIncludedPrice,
    }
  }

  private mapCustomerApiToCustomer(): Pick<Customer, 'firstName' | 'lastName'> {
    return {
      firstName: this.bookingApi.contactPerson!.firstName,
      lastName: this.bookingApi.contactPerson!.lastName,
    }
  }

  private mapInvoicesApiToInvoices(): Invoice[] {
    return (
      this.bookingApi.invoices?.map(invoice => ({
        __typename: 'Invoice',
        createAt: '',
        type: this.mapAccountingDocumentTypeApiToAccountingDocumentType(
          invoice.type,
        ),
        url: invoice.url,
      })) || []
    )
  }

  private mapAccountingDocumentTypeApiToAccountingDocumentType(
    invoiceType: InvoiceApiTypeEnum,
  ): AccountingDocumentType {
    switch (invoiceType) {
      case InvoiceApiTypeEnum.CreditNote:
        return AccountingDocumentType.CreditNote
      case InvoiceApiTypeEnum.Invoice:
        return AccountingDocumentType.Invoice
      default:
        return AccountingDocumentType.Invoice
    }
  }

  private mapCancellationPolicyApiToCancellationPolicies(): CancellationPolicy[] {
    return this.bookingApi.cancellationPolicies.map(
      (policy: CancellationPolicyApi) => ({
        __typename: 'CancellationPolicy',
        dateFrom: policy.dateFrom,
        dateTo: policy.dateTo,
        penaltyPercentage: policy.penaltyPercentage,
      }),
    )
  }

  private mapBookingStatusApiToBookingStatus(): BookingStatus {
    switch (this.bookingApi.status) {
      case BookingApiStatusEnum.Cx:
        return BookingStatus.Canceled
      case BookingApiStatusEnum.Af:
        return BookingStatus.Failed
      case BookingApiStatusEnum.Cf:
        return BookingStatus.Confirmed
      case BookingApiStatusEnum.Created:
        return BookingStatus.Created
      case BookingApiStatusEnum.Ip:
        return BookingStatus.InProgress
      case BookingApiStatusEnum.Pp:
        return BookingStatus.PendingPayment
      case BookingApiStatusEnum.Qr:
        return BookingStatus.QuoteRequest
      case BookingApiStatusEnum.Pc:
        return BookingStatus.PendingConfirm
      default:
        return BookingStatus.Created
    }
  }
}
